import * as d3 from "d3";
import { expressTarget } from "common/api";

export const parseSystemDate = (input) => new Date(input);

export const parseFloatOrNull = (value) =>
  value === "n/a"
    ? null
    : value.endsWith("%") === true
    ? parseFloat((parseFloat(value.trim("%")) / 100).toFixed(4))
    : parseFloat(value);

const fixedPrecision = (value, decimals = 4) =>
  parseFloat(value.toFixed(decimals));

const addVolumeField = (volume) => (share) => ({
  ...share,
  volume: fixedPrecision(volume * share.share, 4),
});

const combineVolumeWithShare = (volume, share) => ({
  ...volume,
  services: share.services.map(addVolumeField(volume.volume)),
});

const projectShareInVolume = (volumes, share) =>
  volumes.length === 0
    ? []
    : share.length === 0
    ? volumes
    : extractAndCombine(volumes, share);

const extractAndCombine = ([current, ...rest], [share, ...nextShare]) =>
  share.time.getTime() === current.time.getTime()
    ? [
        combineVolumeWithShare(current, share),
        ...projectShareInVolume(rest, nextShare),
      ]
    : [current, ...projectShareInVolume(rest, [share, ...nextShare])];

const addSpeedField = (speed) => (share) => ({
  ...share,
  speed: fixedPrecision(speed * share.share, 4),
});

const combineSpeedWithShare = (hourlySpeed, share) => ({
  ...hourlySpeed,
  services: share.services.map(addSpeedField(hourlySpeed.speed)),
});

const projectShareInSpeed = (speeds, share) =>
  speeds.length === 0
    ? []
    : share.length === 0
    ? speeds
    : extractAndCombineSpeed(speeds, share);

const extractAndCombineSpeed = ([current, ...rest], [share, ...nextShare]) =>
  share.time.getTime() === current.time.getTime()
    ? [
        combineSpeedWithShare(current, share),
        ...projectShareInSpeed(rest, nextShare),
      ]
    : [current, ...projectShareInSpeed(rest, [share, ...nextShare])];

const serviceAndLatency = (row) => {
  const [service, latency] = row.split(/\s+/);
  return [
    ["service", service],
    ["latency", parseFloatOrNull(latency)],
  ];
};

export const parseServiceLatencies = (input) => {
  const [_head, ...rows] = input.trim("\n").split("\n");
  return rows.map((row) => Object.fromEntries(serviceAndLatency(row)));
};

const latenciesByTime = (row, [_time, ...bins]) => {
  const [time, ...binValues] = row.split(/\s+/);
  const values = bins.map((bin, index) => ({
    bin,
    share: fixedPrecision(parseFloatOrNull(binValues[index]), 4),
  }));
  return [
    ["time", parseSystemDate(time)],
    ["values", values],
  ];
};
const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S");

export const parseLatenciesByTime=(response) => {
  const [head, ...rows] = response.trim().split("\n");
  const headerAttrs = head.trim().split(/\s+/);
  const headerIndex = headerAttrs.reduce((acc, value, index) => {
    acc[index] = value;
    return acc;
  }, {});

  const data = rows.map((row) => {
    const attrs = row.trim().split(/\s+/);
    return attrs.reduce((acc, attr, index) => {
      const key = headerIndex[index];
      if (key === "TIME") {
        acc[key] = new Date(parseDate(attr));
      } else {
        acc[key] = parseFloat(attr);
      }
      return acc;
    }, {});
  });
  return { data };
}

const latencyTargetOptions = {
  noTarget: "all-subs",
  subsIdKeyword: "subscriber-id",
  addressKeyword: "ip-access",
};
export const expressLatencyTarget = (target) =>
  expressTarget(target, latencyTargetOptions);

export const expressDestination = (destination) =>
  destination === null ? "all-internet" : `ip-internet ${destination}`;

export const parseLatencyDistributions=(response) => {
  if(!response) return [];
  const [header, ...rows] = response.trim().split("\n");
  return rows.reduce((acc, row) => {
    const [key, value] = row.trim().split(/\s+/);
    if (key && value !== undefined) {
      acc.push({ name: key.trim(), value: parseFloat(value.trim()) });
    }
    return acc;
  }, []);
};